'use strict';

import { flow, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';

const EMPTY_COURSE_MESSAGES = { messages: '' };

/**
 * Store for course messages (i.e. messages about a course and how it's related to a student).
 *
 * Because multiple course messages widgets can be on the screen at the same time (e.g. CDP and a course summary popover)
 * this store needs to be instantiated.
 */
class CourseMessagesStore {

    state = States.initial;

    /**
     * Constructs a new instance of the store with the given course code. The instance can be reinitialized by calling
     * initialize().
     *
     * @param courseCode The course code.
     */
    constructor(courseCode) {
        makeObservable(this, {
            state: observable
        });

        if (! courseCode) {
            throw {expectionCode: 500, message: "CourseMessagesStore requires a course code."};
        }
        this.courseMessages = EMPTY_COURSE_MESSAGES;
        this.courseCode = courseCode;
    }

    /**
     * (Re)initialize the store for a new course code.
     * @param courseCode
     */
    initialize(courseCode) {
        this.courseMessages = EMPTY_COURSE_MESSAGES;
        this.courseCode = courseCode;
    }

    /**
     * Load course messages. This data is volatile so it needs to be reloaded each time it's requested. No caching.
     */
    loadCourseMessages = flow(function* () {
        this.state = States.pending;

        // Download course messages only if they are enabled
        let courseMessagesEnabled = appStore.getConfigValue('course_messages_enabled');

        if(courseMessagesEnabled) {
            if (appStore.isDebug()) {
                console.log(`CourseMessagesStore[${this.courseCode}]: Beginning data load.`);
            }
            let url = `api/courses/${encodeURIComponent(this.courseCode)}/messages`;
            try {
                this.courseMessages = yield appStore.getHttpClient().doGet(url, 'get course messages');
                if (appStore.isDebug()) {
                    console.log(`CourseMessagesStore[${this.courseCode}]: Data load complete.`);
                }
                this.state = States.done;
            } catch(error) {
                appStore.getExceptionUtils().handleException(error);
                this.state = States.error;
            }
        }
        else {
            // build an empty result object
            this.courseMessages = EMPTY_COURSE_MESSAGES;
            this.state = States.done;
        }
    }.bind(this));

    /**
     * Getter for the messages.
     */
    getCourseMessages() {
        return this.courseMessages.messages;
    }
}

export default CourseMessagesStore;